import { useState, useEffect, useMemo } from 'react';
import { Autocomplete, TextField, Chip, AutocompleteRenderOptionState } from '@mui/material';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';


type RestorableItem<T> = T & {
  _isRestored?: boolean;
  _savedLabel?: string;
};

interface SavedLabel {
  id: string;
  label: string;
}

interface NimbuxAutocompleteProps<T> {
  data?: T[];
  loading?: boolean;
  searchParams: URLSearchParams;
  paramKey: string;
  updateFilters: (filters: Record<string, string>) => void;
  onSearchChange: (search: string) => void;
  debounceMs?: number;
  getOptionLabel: (option: T) => string;
  getOptionId: (option: T) => string;
  renderOption: (props: React.HTMLAttributes<HTMLLIElement>, option: T, state: AutocompleteRenderOptionState) => React.ReactNode;
  notSelectedOption?: T;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  limitTags?: number;
}

export function NimbuxAutocomplete<T>({
  data = [],
  loading = false,
  searchParams,
  paramKey,
  updateFilters,
  onSearchChange,
  debounceMs = 300,
  getOptionLabel,
  getOptionId,
  renderOption,
  notSelectedOption,
  placeholder,
  size = 'small',
  fullWidth = true,
  limitTags = 1,
}: NimbuxAutocompleteProps<T>) {
  
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useDebouncedState('', debounceMs);
  const [selectedItems, setSelectedItems] = useState<T[]>([]);

  useEffect(() => {
    setSearch(inputValue);
  }, [inputValue, setSearch]);

  useEffect(() => {
    onSearchChange(search);
  }, [search, onSearchChange]);

  useEffect(() => {
    const selectedIds = searchParams.get(paramKey)?.split('|').filter(Boolean) || [];
    
    if (selectedIds.length === 0) {
      setSelectedItems([]);
      localStorage.removeItem(`${paramKey}_labels`);
    } else if (selectedItems.length === 0) {
      try {
        const savedLabels = localStorage.getItem(`${paramKey}_labels`);
        const savedItems: SavedLabel[] = savedLabels ? JSON.parse(savedLabels) : [];
        
        const initialItems = selectedIds.map(id => {
          if (notSelectedOption && getOptionId(notSelectedOption) === id) {
            return notSelectedOption;
          }
          
          const savedItem = savedItems.find((item) => item.id === id);
          return {
            id,
            _savedLabel: savedItem?.label || `ID: ${id}`,
            _isRestored: true,
          } as unknown as T;
        });
        
        setSelectedItems(initialItems);
      } catch (error) {
        console.warn('Error loading saved labels:', error);
        const fallbackItems = selectedIds.map(id => ({
          id,
          _savedLabel: `ID: ${id}`,
          _isRestored: true,
        } as unknown as T));
        setSelectedItems(fallbackItems);
      }
    }
  }, [searchParams, selectedItems.length, notSelectedOption, paramKey, getOptionId]);

  useEffect(() => {
    if (data.length > 0 && selectedItems.length > 0) {
      setSelectedItems(prev => {
        return prev.map(selectedItem => {
          const restorableItem = selectedItem as RestorableItem<T>;
          if ((notSelectedOption && getOptionId(selectedItem) === getOptionId(notSelectedOption)) ||
              (!restorableItem._isRestored)) {
            return selectedItem;
          }
          const fullItem = data.find(item => 
            String(getOptionId(item)) === String(getOptionId(selectedItem))
          );
          return fullItem || selectedItem;
        });
      });
    }
  }, [data, selectedItems, getOptionId, notSelectedOption]);

  const allAvailableOptions = useMemo(() => {
    const searchResults = data || [];
    
    const realSelectedItems = selectedItems.filter(item => {
      if (notSelectedOption && getOptionId(item) === getOptionId(notSelectedOption)) {
        return true;
      }
      return !(item as RestorableItem<T>)._isRestored;
    });
    
    const combined = [...realSelectedItems, ...searchResults];
    
    const uniqueItems = combined.filter((item, index, self) => 
      index === self.findIndex(i => String(getOptionId(i)) === String(getOptionId(item)))
    );
    
    if (notSelectedOption) {
      const hasNotSelected = uniqueItems.some(item => 
        getOptionId(item) === getOptionId(notSelectedOption)
      );
      if (!hasNotSelected) {
        uniqueItems.unshift(notSelectedOption);
      }
    }
    
    return uniqueItems.sort((a, b) => {
      if (notSelectedOption && getOptionId(a) === getOptionId(notSelectedOption)) return -1;
      if (notSelectedOption && getOptionId(b) === getOptionId(notSelectedOption)) return 1;
      const labelA = getOptionLabel(a);
      const labelB = getOptionLabel(b);
      return labelA.localeCompare(labelB);
    });
  }, [selectedItems, data, notSelectedOption, getOptionId, getOptionLabel]);

  const handleChange = (_event: React.SyntheticEvent, value: T[]) => {
    setSelectedItems(value);
    const valuesForUrl = value.map(val => getOptionId(val)).join('|');
    
    const itemsData = value.map(val => ({
      id: getOptionId(val),
      label: getOptionLabel(val)
    }));
    localStorage.setItem(`${paramKey}_labels`, JSON.stringify(itemsData));
    
    updateFilters({ [paramKey]: valuesForUrl });
  };

  const handleInputChange = (_event: React.SyntheticEvent, value: string, reason: string) => {
    if (reason === 'input') {
      setInputValue(value);
    }
  };

  return (
    <Autocomplete
      fullWidth={fullWidth}
      limitTags={limitTags}
      size={size}
      multiple
      loading={loading}
      options={allAvailableOptions}
      getOptionLabel={getOptionLabel}
      value={selectedItems}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      clearOnBlur={false}
      clearOnEscape={false}
      renderTags={(tagValue, getTagProps) => {
        return tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          
          const restorableOption = option as RestorableItem<T>;
          const label = restorableOption._isRestored 
            ? restorableOption._savedLabel || getOptionLabel(option)
            : getOptionLabel(option);
          
          return (
            <Chip
              key={key}
              size="small"
              label={label}
              {...tagProps}
              sx={{ mr: 1 }}
            />
          );
        });
      }}
      renderInput={(params) => (
        <TextField {...params} variant="outlined" fullWidth placeholder={placeholder} />
      )}
      renderOption={renderOption}
    />
  );
} 
